import AppComponent from '../base/app-component.ts';
import { apiGet, apiPost } from '../lib/api-client.ts';
import type { DataTable } from './data-table.ts';

type Tipo = 'EDC' | 'ALT';

interface Riga {
  id: number;
  cognome: string;
  nome: string;
  cf: string | null;
  // ALT
  categoria?: string;
  categoriaNome?: string | null;
  data_iscrizione?: string;
  // CE
  data_conferma?: string | null;
  impresa_ragione_sociale?: string | null;
  codice_cassa?: string;
  periodo?: string | null;
}

interface TrasmissibiliResponse {
  righe: Riga[];
  totale: number;
}

type InviaResponse =
  | {
      ok: true;
      invioId: number | null;
      totale: number;
      inviati: number;
      errori: number;
      codiceInserimento: string | null;
      dryRun: boolean;
      endpointOk: boolean;
    }
  | { ok: false; invioId: number | null; errore: string };

const PAGE_SIZE = 30;

const MESI_BREVI = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];

function formatPeriodo(yyyymm: string): string {
  if (!/^\d{6}$/.test(yyyymm)) return yyyymm;
  const anno = yyyymm.slice(0, 4);
  const mese = Number(yyyymm.slice(4, 6));
  if (mese < 1 || mese > 12) return yyyymm;
  return `${MESI_BREVI[mese - 1]} ${anno}`;
}

interface FiltriAvanzati {
  codice_cassa?: string;   // CE
  periodo?: string;        // CE (YYYYMM)
  categoria?: string;      // ALT
  data_da?: string;        // entrambi
  data_a?: string;         // entrambi
}

/**
 * wc-statini-invio - Wrapper sopra <wc-data-table> (bulk-select) per
 * /statini/invio. Una istanza per tab (`tipo="EDC"` o `"ALT"`).
 *
 * Caricamento totale + filtro/paginazione client-side (semplifica multi-filtro).
 */
export class StatiniInvio extends AppComponent {
  static override componentName = 'wc-statini-invio';
  protected override get templateId() { return 'statini-invio'; }

  private tutte: Riga[] = [];
  private filtrate: Riga[] = [];
  private currentPage = 1;
  private filterText = '';
  private filtriAvanzati: FiltriAvanzati = {};
  private selectedCount = 0;
  private inFlight = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private offcanvas: any = null;

  protected override afterRender(): void {
    const dt = this.dataTable;
    if (!dt) return;

    this.listen(dt, 'dt-filter-change', (e: Event) => {
      this.filterText = ((e as CustomEvent).detail.filter as string).toLowerCase();
      this.currentPage = 1;
      this.applyFilterAndRender();
    });
    this.listen(dt, 'dt-page-change', (e: Event) => {
      this.currentPage = (e as CustomEvent).detail.page as number;
      this.renderPage();
    });
    this.listen(dt, 'dt-selection-change', (e: Event) => {
      this.selectedCount = (e as CustomEvent).detail.count as number;
      this.updateToolbar();
    });

    this.delegate.on('click', '[data-action="invia-selezionati"]', () => {
      const keys = dt.getSelectedKeys().map(k => Number(k));
      void this.invia(keys);
    });
    this.delegate.on('click', '[data-action="invia-tutti"]', () => {
      void this.invia([]);
    });

    this.setupFiltri();
    void this.load();
  }

  private setupFiltri(): void {
    const offEl = this.$('[data-offcanvas="filtri"]');
    if (offEl) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.offcanvas = new (globalThis as any).bootstrap.Offcanvas(offEl);
    }

    this.delegate.on('click', '[data-action="apri-filtri"]', () => {
      this.offcanvas?.show();
    });

    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', (e: Event) => {
        e.preventDefault();
        this.raccogliFiltriAvanzati();
        this.offcanvas?.hide();
        this.currentPage = 1;
        this.applyFilterAndRender();
      });
    }

    this.delegate.on('click', '[data-action="reset-filtri"], [data-action="reset-filtri-toolbar"]', () => {
      this.resetFiltri();
      this.offcanvas?.hide();
      this.currentPage = 1;
      this.applyFilterAndRender();
    });
  }

  private raccogliFiltriAvanzati(): void {
    const fields: (keyof FiltriAvanzati)[] =
      this.tipo === 'EDC'
        ? ['codice_cassa', 'periodo', 'data_da', 'data_a']
        : ['categoria', 'data_da', 'data_a'];
    const next: FiltriAvanzati = {};
    for (const f of fields) {
      const el = this.$(`[data-filtro-val="${f}"]`) as HTMLInputElement | HTMLSelectElement | null;
      const v = el?.value.trim();
      if (v) next[f] = v;
    }
    this.filtriAvanzati = next;
  }

  private resetFiltri(): void {
    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    form?.reset();
    this.filtriAvanzati = {};
    this.filterText = '';
    const searchInput = this.$('[data-dt-filter-input]') as HTMLInputElement | null;
    if (searchInput) searchInput.value = '';
    this.updateFiltriCount();
  }

  private updateFiltriCount(): void {
    const count = Object.values(this.filtriAvanzati).filter(Boolean).length;
    const badge = this.$('[data-filtri-count]');
    if (badge) {
      badge.textContent = count > 0 ? String(count) : '';
      badge.classList.toggle('d-none', count === 0);
    }
    const resetBtn = this.$('[data-action="reset-filtri-toolbar"]');
    resetBtn?.classList.toggle('d-none', count === 0);
  }

  private get dataTable(): DataTable | null {
    return this.$('wc-data-table') as DataTable | null;
  }

  private get tipo(): Tipo {
    return this.attributeString('tipo') === 'EDC' ? 'EDC' : 'ALT';
  }

  private isDryRun(): boolean {
    const id = this.attributeString('data-dry-run-source') || 'statini-dry-run';
    const el = document.getElementById(id) as HTMLInputElement | null;
    return el?.checked ?? false;
  }

  private async load(): Promise<void> {
    const dt = this.dataTable;
    if (!dt) return;
    dt.setLoading();
    const r = await apiGet<TrasmissibiliResponse>(`statini/trasmissibili?tipo=${this.tipo}`);
    if (!r.ok) {
      this.tutte = [];
      this.filtrate = [];
      dt.setError(r.error);
      this.updateToolbar();
      return;
    }
    this.tutte = r.data.righe;
    this.currentPage = 1;
    dt.clearSelection();
    this.popolaFiltriDinamici();
    this.applyFilterAndRender();
  }

  /**
   * Deriva i valori distinti per i filtri rapidi/avanzati direttamente
   * dal dataset caricato. Nessun hardcoding, zero accoppiamento con config.
   */
  private popolaFiltriDinamici(): void {
    if (this.tipo === 'EDC') {
      const casse = this.distinct(r => r.codice_cassa).sort();
      this.renderSelect('codice_cassa', casse.map(c => ({ value: c, label: c })));
      const periodi = this.distinct(r => r.periodo).sort().reverse();
      this.renderSelect('periodo', periodi.map(p => ({ value: p, label: formatPeriodo(p) })));
    } else {
      const cats = this.distinct(r => r.categoria).sort();
      const catNomi = new Map<string, string>();
      for (const r of this.tutte) {
        if (r.categoria && !catNomi.has(r.categoria)) {
          catNomi.set(r.categoria, r.categoriaNome ?? r.categoria);
        }
      }
      this.renderSelect('categoria', cats.map(c => ({
        value: c,
        label: `${c} - ${catNomi.get(c) ?? c}`,
      })));
    }
  }

  private distinct(extractor: (r: Riga) => string | null | undefined): string[] {
    const set = new Set<string>();
    for (const r of this.tutte) {
      const v = extractor(r);
      if (v) set.add(v);
    }
    return Array.from(set);
  }

  private renderSelect(campo: string, items: Array<{ value: string; label: string }>): void {
    const sel = this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null;
    if (!sel) return;
    const placeholder = sel.querySelector('option[value=""]');
    sel.innerHTML = '';
    if (placeholder) sel.appendChild(placeholder);
    for (const it of items) {
      const opt = document.createElement('option');
      opt.value = it.value;
      opt.textContent = it.label;
      sel.appendChild(opt);
    }
  }

  private applyFilterAndRender(): void {
    const q = this.filterText;
    const { codice_cassa, periodo, categoria, data_da, data_a } = this.filtriAvanzati;
    this.filtrate = this.tutte.filter(r => {
      if (q) {
        const inText = (r.cognome || '').toLowerCase().includes(q)
          || (r.nome || '').toLowerCase().includes(q)
          || (r.cf || '').toLowerCase().includes(q);
        if (!inText) return false;
      }
      if (codice_cassa && r.codice_cassa !== codice_cassa) return false;
      if (periodo && r.periodo !== periodo) return false;
      if (categoria && r.categoria !== categoria) return false;
      const dataRiga = this.tipo === 'EDC' ? r.data_conferma : r.data_iscrizione;
      if (data_da && (!dataRiga || dataRiga < data_da)) return false;
      if (data_a && (!dataRiga || dataRiga > data_a)) return false;
      return true;
    });
    this.updateFiltriCount();
    this.renderPage();
  }

  private renderPage(): void {
    const dt = this.dataTable;
    if (!dt) return;
    const totale = this.filtrate.length;
    const totalPages = Math.max(1, Math.ceil(totale / PAGE_SIZE));
    if (this.currentPage > totalPages) this.currentPage = totalPages;
    const start = (this.currentPage - 1) * PAGE_SIZE;
    const slice = this.filtrate.slice(start, start + PAGE_SIZE);
    dt.setData(slice as unknown as Record<string, unknown>[], {
      page: this.currentPage,
      totalPages,
      count: totale,
      pageSize: PAGE_SIZE,
    });
    this.updateToolbar();
  }

  private updateToolbar(): void {
    const totale = this.filtrate.length;
    const counter = this.$('[data-counter]');
    if (counter) counter.textContent = `Selezionati: ${this.selectedCount} / Totale: ${totale}`;
    const btnSel = this.$('[data-action="invia-selezionati"]') as HTMLButtonElement | null;
    const btnAll = this.$('[data-action="invia-tutti"]') as HTMLButtonElement | null;
    if (btnSel) btnSel.disabled = this.inFlight || this.selectedCount === 0;
    if (btnAll) btnAll.disabled = this.inFlight || this.filtrate.length === 0;
  }

  private async invia(ids: number[]): Promise<void> {
    if (this.inFlight) return;
    const isAll = ids.length === 0;
    const n = isAll ? this.filtrate.length : ids.length;
    if (n === 0) return;

    const dryRun = this.isDryRun();
    const tipoLabel = this.tipo === 'EDC' ? 'Cassa Edile' : 'Altri Settori';
    const dryLabel = dryRun ? ' (dry-run)' : '';
    const msg = isAll
      ? `Inviare a Statini ${n} nominativo/i (${tipoLabel})${dryLabel}?`
      : `Inviare a Statini ${n} nominativo/i selezionato/i (${tipoLabel})${dryLabel}?`;
    const ok = await Weext.confirm(msg, { title: 'Conferma invio', confirmText: 'Invia' });
    if (!ok) return;

    this.setLoading(true, isAll);
    const payloadIds = isAll ? this.filtrate.map(r => r.id) : ids;
    const r = await apiPost<InviaResponse>('statini/invia', {
      tipo: this.tipo,
      ids: payloadIds.length === this.tutte.length ? [] : payloadIds,
      dryRun,
    });
    this.setLoading(false, isAll);

    if (!r.ok) {
      Weext.toast(r.error || 'Errore invio', { type: 'danger' });
      return;
    }
    this.showEsito(r.data);
    if (r.data.ok && r.data.endpointOk && !r.data.dryRun) {
      this.resetFiltri();
    }
    void this.load();
  }

  private setLoading(loading: boolean, isAll: boolean): void {
    this.inFlight = loading;
    const spinSel = this.$('[data-spinner-sel]');
    const spinAll = this.$('[data-spinner-all]');
    if (spinSel) spinSel.classList.toggle('d-none', !(loading && !isAll));
    if (spinAll) spinAll.classList.toggle('d-none', !(loading && isAll));
    this.updateToolbar();
  }

  private showEsito(res: InviaResponse): void {
    if (!res.ok) {
      Weext.toast(res.errore || 'Errore invio', { type: 'danger' });
      return;
    }
    if (!res.endpointOk) {
      Weext.toast('Errore di trasmissione. Provare di nuovo.', { type: 'danger' });
      return;
    }
    const dry = res.dryRun ? ' (dry-run)' : '';
    if (res.totale === 0) {
      Weext.toast('Nessun nominativo da inviare', { type: 'info' });
      return;
    }
    if (res.errori > 0) {
      Weext.toast(
        `Inviati: ${res.inviati} / Totali: ${res.totale}, Errori: ${res.errori}${dry}`,
        { type: res.inviati > 0 ? 'warning' : 'danger' },
      );
      return;
    }
    Weext.toast(`Inviati: ${res.inviati}${dry}`, { type: 'success' });
  }

  protected override onDisconnected(): void {
    this.offcanvas?.dispose();
  }
}
